import { AnimatePresence, motion } from "framer-motion";
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { HudPanel } from "../common/HudPanel";
import { HudStatusPill, type HudStatusTone } from "../common/HudStatusPill";
import {
  type ChannelArtifactDetail,
  type ChannelArtifactSectionBody,
  type ChannelArtifactSectionToc,
  type ChannelArtifactSummary,
  type ChannelArtifactTransport,
  type ChannelArtifactVersion,
  artifactMarkdownFilename,
  countArtifactsByChannel,
  fetchChannelArtifactDetail,
  fetchChannelArtifactMarkdown,
  fetchChannelArtifactSection,
  fetchChannelArtifactVersions,
  fetchChannelArtifacts,
  sortArtifactsByRecency,
} from "./channelArtifacts";

// ---------------------------------------------------------------------------
// Channel-bound artifacts panel — list + detail.
//
// Shows persistent typed outputs the actors built in their channels: briefs,
// Reports, plans (markdown only in v1). Two-pane layout: left rail lists
// Artifacts in the selected channel, right pane shows head + sections + version
// History for the selected artifact. Section bodies drill on click.
//
// Channel-scoped, not actor-scoped: every read goes through
// /user/channels/:channelId/artifacts..., authorized by organization ownership
// of the channel. So a memberless, archived, or retained-history channel still
// renders its artifacts — the record outlives the actors that wrote it. There
// is no channel-scoped write route, which is what makes those channels
// read-only by construction rather than by a UI flag.
// ---------------------------------------------------------------------------

interface ChannelArtifactsPanelProps {
  channels: { id: string; displayName: string }[];
  selectedChannelId: string | null;
  /** Called whenever artifact counts per channel change. Lets the parent
   *  surface counts on the channel scope chips. */
  onCountsChange?: (
    countsByChannelId: ReadonlyMap<string, number>,
    total: number,
  ) => void;
  /** Seed for the selected artifact (restored when the panel remounts after a
   *  pivot). `onSelectedArtifactChange` reports selection back so the parent
   *  can persist it per channel. Both optional — self-contained without them. */
  initialSelectedArtifactId?: string | null;
  onSelectedArtifactChange?: (artifactId: string | null) => void;
  /** Test seam only: overrides forwarded to the generated client. */
  transport?: ChannelArtifactTransport;
}

const REFRESH_INTERVAL_MS = 30_000;

function statusTone(status: string): HudStatusTone {
  switch (status) {
    case "complete": {
      return "success";
    }
    case "deleted": {
      return "danger";
    }
    case "draft":
    default: {
      return "neutral";
    }
  }
}

function formatRelative(iso: string): string {
  const ts = new Date(iso).getTime();
  const diff = Date.now() - ts;
  if (diff < 60_000) {
    return "now";
  }
  if (diff < 3_600_000) {
    return `${Math.floor(diff / 60_000)}m ago`;
  }
  if (diff < 86_400_000) {
    return `${Math.floor(diff / 3_600_000)}h ago`;
  }
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}Z`;
}

/** Client-side save of an already-fetched blob — no upload, no new route. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

const ArtifactListRow: FC<{
  artifact: ChannelArtifactSummary;
  channelName: string | null;
  selected: boolean;
  onSelect: () => void;
}> = ({ artifact, channelName, selected, onSelect }) => (
  <motion.button
    animate={{ opacity: 1, x: 0 }}
    className={`group flex w-full flex-col gap-1.5 border-b border-slate-200/60 px-3 py-2.5 text-left transition-colors dark:border-slate-800/60 ${
      selected
        ? "bg-sky-500/10 dark:bg-sky-400/5"
        : "hover:bg-slate-100/60 dark:hover:bg-slate-800/40"
    }`}
    initial={{ opacity: 0, x: -8 }}
    onClick={onSelect}
    transition={{ damping: 24, stiffness: 280, type: "spring" }}
    type="button"
    whileHover={{ x: 2 }}
  >
    <div className="flex min-w-0 items-center gap-2">
      <span className="min-w-0 truncate font-mono text-sm text-slate-800 dark:text-slate-200">
        #{artifact.slug}
      </span>
      <span className="flex-shrink-0 border border-slate-300 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tabular-nums tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
        v{artifact.current_version}
      </span>
      <span className="flex-shrink-0">
        <HudStatusPill
          label={artifact.status.toUpperCase()}
          tone={statusTone(artifact.status)}
        />
      </span>
    </div>
    <span className="line-clamp-1 text-xs text-slate-700 dark:text-slate-300">
      {artifact.title}
    </span>
    <span className="line-clamp-1 text-[11px] text-slate-500 dark:text-slate-500">
      {artifact.excerpt}
    </span>
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-slate-500">
      {channelName ? `#${channelName} · ` : ""}
      last edit · {artifact.created_by_instance_id.slice(0, 6)} ·{" "}
      {formatRelative(artifact.updated_at)}
    </span>
  </motion.button>
);

const SectionTocRow: FC<{
  section: ChannelArtifactSectionToc;
  expanded: boolean;
  body: ChannelArtifactSectionBody | null;
  loading: boolean;
  onToggle: () => void;
}> = ({ section, expanded, body, loading, onToggle }) => {
  const charTone =
    section.char_count > 2000
      ? "border-amber-500/50 text-amber-700 dark:border-amber-400/50 dark:text-amber-300"
      : "border-sky-500/50 text-sky-700 dark:border-sky-400/50 dark:text-sky-300";
  return (
    <div className="border-b border-slate-200/60 last:border-b-0 dark:border-slate-800/60">
      <button
        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-100/60 dark:hover:bg-slate-800/40"
        onClick={onToggle}
        type="button"
      >
        <span className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
            ##
          </span>
          <span className="font-mono text-sm text-slate-800 dark:text-slate-200">
            {section.title}
          </span>
        </span>
        <span
          className={`border px-1.5 py-0.5 font-mono text-[9px] tabular-nums ${charTone}`}
        >
          {section.char_count} CH
        </span>
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            animate={{ height: "auto", opacity: 1 }}
            className="overflow-hidden"
            exit={{ height: 0, opacity: 0 }}
            initial={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="px-3 pb-3 pt-1">
              {loading && (
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  [ LOADING ]
                </div>
              )}
              {body && (
                <pre className="whitespace-pre-wrap break-words font-mono text-[12px] leading-relaxed text-slate-700 dark:text-slate-300">
                  {body.body}
                </pre>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const VersionTimelineRow: FC<{
  version: ChannelArtifactVersion;
  isLatest: boolean;
}> = ({ version, isLatest }) => (
  <motion.div
    animate={{ opacity: 1, x: 0 }}
    className="flex items-start gap-3 px-3 py-2"
    initial={{ opacity: 0, x: -16 }}
    transition={{ damping: 24, stiffness: 280, type: "spring" }}
  >
    <span
      className={`mt-0.5 border px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tabular-nums ${
        isLatest
          ? "border-emerald-500/50 text-emerald-700 dark:border-emerald-400/50 dark:text-emerald-300"
          : "border-slate-400/60 text-slate-500"
      }`}
    >
      [ V{version.version} ]
    </span>
    <div className="flex flex-col gap-0.5">
      <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
        {version.author_instance_id.slice(0, 6)} ·{" "}
        {formatTimestamp(version.created_at)}
      </span>
      {version.op_summary && (
        <span className="text-[12px] text-slate-700 dark:text-slate-300">
          {version.op_summary}
        </span>
      )}
      {version.reason && (
        <span className="text-[11px] italic text-slate-500">
          "{version.reason}"
        </span>
      )}
    </div>
  </motion.div>
);

const ArtifactDetailPane: FC<{
  channelId: string;
  detail: ChannelArtifactDetail;
  transport?: ChannelArtifactTransport;
  versions: ChannelArtifactVersion[];
}> = ({ channelId, detail, transport, versions }) => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [sectionBodies, setSectionBodies] = useState<
    Record<string, ChannelArtifactSectionBody>
  >({});
  const [loadingSection, setLoadingSection] = useState<string | null>(null);
  const [downloadState, setDownloadState] = useState<
    "error" | "idle" | "saving"
  >("idle");

  // Markdown export is assembled in the browser from reads the pane already
  // has routes for: the detail head plus each section body (cached ones are
  // reused). Nothing is uploaded and no export route is involved.
  const onDownloadMarkdown = useCallback(async () => {
    setDownloadState("saving");
    try {
      const markdown = await fetchChannelArtifactMarkdown(
        channelId,
        detail,
        sectionBodies,
        transport,
      );
      saveBlob(
        new Blob([markdown], { type: "text/markdown;charset=utf-8" }),
        artifactMarkdownFilename(detail),
      );
      setDownloadState("idle");
    } catch {
      // A failed section read must not tear down the pane; the button just
      // reports and stays clickable for a retry.
      setDownloadState("error");
    }
  }, [channelId, detail, sectionBodies, transport]);

  // The version a settling section read must still match to be written. A read
  // started before the artifact advanced is stale by the time it returns, and
  // the request closure only knows the version it asked for.
  const currentVersionRef = useRef(detail.current_version);

  // Cached bodies belong to the version they were read at. When the artifact
  // advances while it is selected, the pane must not keep showing — or
  // downloading — a body from the edit before.
  useEffect(() => {
    currentVersionRef.current = detail.current_version;
    setSectionBodies((prev) =>
      Object.values(prev).every(
        (body) => body.artifact_version === detail.current_version,
      )
        ? prev
        : {},
    );
  }, [detail.current_version, detail.id]);

  const onToggleSection = useCallback(
    async (sectionId: string) => {
      const next = expandedSection === sectionId ? null : sectionId;
      setExpandedSection(next);
      if (
        next &&
        sectionBodies[next]?.artifact_version !== detail.current_version
      ) {
        setLoadingSection(next);
        try {
          const body = await fetchChannelArtifactSection(
            channelId,
            detail.id,
            next,
            detail.current_version,
            transport,
          );
          // An in-flight read that outlived its version is dropped rather than
          // written: the cache-clearing effect above only runs on the version
          // change itself, so a later write would repopulate it.
          if (body.artifact_version === currentVersionRef.current) {
            setSectionBodies((prev) => ({ ...prev, [next]: body }));
          }
        } catch {
          // A failed section read leaves the row expanded-but-empty rather
          // than tearing down the whole detail pane; retry is a re-click.
        } finally {
          setLoadingSection(null);
        }
      }
    },
    [
      channelId,
      detail.current_version,
      detail.id,
      expandedSection,
      sectionBodies,
      transport,
    ],
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="px-3 pt-3">
        <div className="mb-1 flex items-center gap-2">
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            #{detail.slug}
          </span>
          <span className="border border-slate-300 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase tabular-nums tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400">
            v{detail.current_version}
          </span>
          <HudStatusPill
            label={detail.status.toUpperCase()}
            tone={statusTone(detail.status)}
          />
          <button
            className="ml-auto flex-shrink-0 border border-sky-500/60 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-sky-700 transition-colors hover:bg-sky-500/10 disabled:opacity-60 dark:border-sky-400/60 dark:text-sky-300"
            disabled={downloadState === "saving"}
            onClick={() => void onDownloadMarkdown()}
            title="Download this artifact as a markdown file"
            type="button"
          >
            {downloadState === "saving" ? "↓ …" : "↓ .MD"}
          </button>
        </div>
        {downloadState === "error" && (
          <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-rose-500">
            [ DOWNLOAD FAILED · RETRY ]
          </div>
        )}
        <h2 className="font-mono text-lg text-slate-900 dark:text-slate-100">
          {detail.title}
        </h2>
        {detail.lead && (
          <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
            {detail.lead}
          </p>
        )}
      </div>

      <div>
        <div className="border-b border-slate-300/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:border-slate-700/60">
          [ SECTIONS · {detail.sections.length} ]
        </div>
        <motion.div
          animate="visible"
          initial="hidden"
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.04 } },
          }}
        >
          {detail.sections.map((s) => (
            <SectionTocRow
              key={s.id}
              // Render coherence: a body is shown only under the version label
              // it was read at, never the one the head now advertises.
              body={
                sectionBodies[s.id]?.artifact_version === detail.current_version
                  ? sectionBodies[s.id]
                  : null
              }
              expanded={expandedSection === s.id}
              loading={loadingSection === s.id}
              onToggle={() => void onToggleSection(s.id)}
              section={s}
            />
          ))}
        </motion.div>
      </div>

      <div>
        <div className="border-b border-slate-300/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:border-slate-700/60">
          [ VERSION HISTORY · {versions.length} ]
        </div>
        <div className="border-l border-dashed border-slate-300/60 ml-5 dark:border-slate-700/60">
          {versions.map((v, idx) => (
            <VersionTimelineRow key={v.id} isLatest={idx === 0} version={v} />
          ))}
        </div>
      </div>
    </div>
  );
};

export const ChannelArtifactsPanel: FC<ChannelArtifactsPanelProps> = ({
  channels,
  selectedChannelId,
  onCountsChange,
  initialSelectedArtifactId = null,
  onSelectedArtifactChange,
  transport,
}) => {
  const [allArtifacts, setAllArtifacts] = useState<ChannelArtifactSummary[]>(
    [],
  );
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(
    initialSelectedArtifactId,
  );
  // Report selection up so the parent can persist it per channel and seed it on
  // the next remount. The parent's setter no-ops on an unchanged value.
  useEffect(() => {
    onSelectedArtifactChange?.(selectedArtifactId);
  }, [selectedArtifactId, onSelectedArtifactChange]);
  const [detail, setDetail] = useState<ChannelArtifactDetail | null>(null);
  const [versions, setVersions] = useState<ChannelArtifactVersion[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState<boolean>(false);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);

  const allChannelIdsKey = useMemo(
    () =>
      channels
        .map((channel) => channel.id)
        .toSorted()
        .join("|"),
    [channels],
  );

  // Reset selection only on a genuine channel CHANGE, not on mount — skipping
  // the initial run lets `initialSelectedArtifactId` seed survive (the Inspector
  // remounts this panel per channel, where the mount run would otherwise null
  // the restored selection).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    setSelectedArtifactId(null);
  }, [selectedChannelId]);

  // Fetch artifacts for all channels regardless of filter so we always know
  // The full per-channel counts (parent surfaces them on chip strip). Filter
  // is applied locally at render time. Periodic refresh for live updates.
  useEffect(() => {
    const channelIds = channels.map((channel) => channel.id);
    if (channelIds.length === 0) {
      setAllArtifacts([]);
      return undefined;
    }
    let cancelled = false;
    const fetchList = async () => {
      try {
        setLoadingList(true);
        const artifactLists = await Promise.all(
          channelIds.map(
            async (channelId) =>
              await fetchChannelArtifacts(channelId, transport),
          ),
        );
        const nextArtifacts = sortArtifactsByRecency(artifactLists.flat());
        if (!cancelled) {
          setAllArtifacts(nextArtifacts);
          setErrorMessage(null);
          setLoadingList(false);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : String(error),
          );
          setLoadingList(false);
        }
      }
    };
    void fetchList();
    const interval = setInterval(fetchList, REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [allChannelIdsKey, channels, transport]);

  // The channel that owns the selected artifact — the artifact routes are
  // scoped by it, and the panel can be showing rows from several channels at
  // once (no filter), so this comes from the row rather than from
  // `selectedChannelId`.
  const selectedArtifactChannelId = useMemo(
    () =>
      allArtifacts.find((artifact) => artifact.id === selectedArtifactId)
        ?.channel_id ?? null,
    [allArtifacts, selectedArtifactId],
  );

  // Fetch detail + versions whenever selectedArtifactId changes.
  useEffect(() => {
    if (!selectedArtifactId || !selectedArtifactChannelId) {
      setDetail(null);
      setVersions([]);
      return undefined;
    }
    let cancelled = false;
    const fetchDetail = async () => {
      try {
        setLoadingDetail(true);
        const [detailJson, versionsJson] = await Promise.all([
          fetchChannelArtifactDetail(
            selectedArtifactChannelId,
            selectedArtifactId,
            transport,
          ),
          fetchChannelArtifactVersions(
            selectedArtifactChannelId,
            selectedArtifactId,
            transport,
          ),
        ]);
        if (!cancelled) {
          setDetail(detailJson);
          setVersions(versionsJson);
          setLoadingDetail(false);
        }
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            error instanceof Error ? error.message : String(error),
          );
          setLoadingDetail(false);
        }
      }
    };
    void fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedArtifactChannelId, selectedArtifactId, transport]);

  // Apply channel filter at display time (panel always fetches all channels).
  const artifacts = useMemo(
    () =>
      selectedChannelId
        ? allArtifacts.filter((a) => a.channel_id === selectedChannelId)
        : allArtifacts,
    [allArtifacts, selectedChannelId],
  );

  // Publish per-channel artifact counts to the parent (drives chip strip).
  const countsByChannelId = useMemo(
    () => countArtifactsByChannel(allArtifacts),
    [allArtifacts],
  );

  useEffect(() => {
    onCountsChange?.(countsByChannelId, allArtifacts.length);
  }, [allArtifacts.length, countsByChannelId, onCountsChange]);

  // When the artifact list refreshes, drop selectedArtifactId if it's no longer present.
  useEffect(() => {
    if (
      selectedArtifactId &&
      !artifacts.some((a) => a.id === selectedArtifactId)
    ) {
      setSelectedArtifactId(null);
    }
  }, [artifacts, selectedArtifactId]);

  const channelLabel = useMemo(
    () =>
      selectedChannelId
        ? (channels.find((c) => c.id === selectedChannelId)?.displayName ?? "?")
        : "all channels",
    [channels, selectedChannelId],
  );
  const channelNameById = useMemo(
    () => new Map(channels.map((channel) => [channel.id, channel.displayName])),
    [channels],
  );

  return (
    <div className="flex h-full min-h-0 flex-col p-0 sm:p-3">
      <HudPanel
        bodyClassName="p-0 flex flex-1 min-h-0 flex-col"
        className="flex flex-1 min-h-0 flex-col"
        subtitle={`CHANNEL · ${selectedChannelId ? `#${channelLabel.toUpperCase()}` : channelLabel.toUpperCase()}`}
        title="CHANNEL ARTIFACTS"
      >
        {/* Single-column master→detail at every width: this panel lives in a
            side pane (room hub / drawer), never the full page, so a two-pane
            split just cramps both halves. List → select → detail with a back
            control. */}
        <div className="flex min-h-0 flex-1 flex-col">
          {/* List pane — shown until an artifact is selected. */}
          <div
            className={`min-h-0 flex-col overflow-y-auto ${
              selectedArtifactId ? "hidden" : "flex flex-1"
            }`}
          >
            {loadingList && artifacts.length === 0 && (
              <div className="px-3 py-8 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                [ LOADING ]
              </div>
            )}
            {errorMessage && (
              <div className="px-3 py-8 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-rose-500">
                [ ERROR · {errorMessage} ]
              </div>
            )}
            {!loadingList && !errorMessage && artifacts.length === 0 && (
              <div className="px-3 py-8 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                [ NO ARTIFACTS IN SCOPE ]
              </div>
            )}
            {artifacts.map((a) => (
              <ArtifactListRow
                key={a.id}
                artifact={a}
                channelName={channelNameById.get(a.channel_id) ?? null}
                onSelect={() => setSelectedArtifactId(a.id)}
                selected={a.id === selectedArtifactId}
              />
            ))}
          </div>
          {/* Detail pane — shown when an artifact is selected, with a back
              control to return to the list. */}
          <div
            className={`min-h-0 flex-col overflow-y-auto ${
              selectedArtifactId ? "flex flex-1" : "hidden"
            }`}
          >
            {selectedArtifactId && (
              <button
                className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-300/60 bg-slate-50/95 px-3 py-2 text-left font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-600 backdrop-blur transition-colors hover:text-slate-900 dark:border-slate-700/60 dark:bg-slate-900/95 dark:text-slate-400 dark:hover:text-slate-100"
                onClick={() => setSelectedArtifactId(null)}
                type="button"
              >
                ← BACK TO LIST
              </button>
            )}
            <AnimatePresence mode="wait">
              {!selectedArtifactId && (
                <motion.div
                  key="empty"
                  animate={{ opacity: 1 }}
                  className="flex h-full items-center justify-center px-6 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                >
                  [ SELECT AN ARTIFACT TO INSPECT ]
                </motion.div>
              )}
              {selectedArtifactId && loadingDetail && !detail && (
                <motion.div
                  key="loading"
                  animate={{ opacity: 1 }}
                  className="flex h-full items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500"
                  exit={{ opacity: 0 }}
                  initial={{ opacity: 0 }}
                >
                  [ LOADING ]
                </motion.div>
              )}
              {selectedArtifactId && detail && selectedArtifactChannelId && (
                <AnimatedArtifactDetail
                  key={detail.id}
                  channelId={selectedArtifactChannelId}
                  detail={detail}
                  transport={transport}
                  versions={versions}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </HudPanel>
    </div>
  );
};

function AnimatedArtifactDetail({
  channelId,
  detail,
  transport,
  versions,
}: React.ComponentProps<typeof ArtifactDetailPane>) {
  return (
    <motion.div
      key={detail.id}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      initial={{ opacity: 0, y: 8 }}
      transition={{ damping: 26, stiffness: 320, type: "spring" }}
    >
      <ArtifactDetailPane
        channelId={channelId}
        detail={detail}
        transport={transport}
        versions={versions}
      />
    </motion.div>
  );
}
